import React, { useState, useMemo, useEffect } from 'react';
import {
    Search,
    Plus,
    Eye,
    Edit,
    Trash2,
    Calendar,
    Users,
    Gift,
    Package,
    CreditCard,
    BarChart3,
    Settings,
    ChevronDown,
    X,
    Phone,
    Mail,
    MapPin,
    DollarSign,
    TrendingUp,
    User,
    Download
} from 'lucide-react';
import { ClientTable } from "./components/clientTable/clientTable";
import { useClients } from "./useClients";
import { useClientDrawer } from "./components/drawer/useClientDrawer";
import ViewClientModal from "./components/viewClientModal/ViewClientModal";
import AddClientModal from "./components/components/AddClientModal";
import { useManageClients } from "./useManageClients";
import { DeleteClientModal } from "./components/components/deleteClientModal";
import BulkCsvUpload from "../components/BulkCsvUpload";
import { Loading } from "app/components/loading/loading";

interface Client {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    street: string;
    city: string;
    state: string;
    zipCode: string;
    totalSpent: number;
    visits: number;
    lastVisit: string;
    joinDate: string;
    birthday?: string;
    notes?: string;
}

interface Visit {
    id: number;
    clientId: number;
    date: string;
    service: string;
    amount: number;
    notes?: string;
}

const ClientsPage = () => {

    const {
        rowsPerPage,
        setRowsPerPage,
        sortBy,
        setSortBy,
        shouldShowClientDrawer,
        setShouldShowClientDrawer,
        onClientRowClick,
        clientIdParam,
        onOpenNewClientDrawer,
        loading,
        isSearching,
        totalCount,
        shouldShowBlacklistDrawer,
        setShouldShowBlacklistDrawer,
        onBlacklistAdd,
        onBlacklistRemove,
        navigate,
        account,
        managerIdsToFilter,
        setManagerIdsToFilter,
        fetchClients,
        searchTerm,
        onSearchChange,
        exportClients,
    } = useClients();


    const { onSubmit, showViewModal, handleCloseViewClient, setShowViewModal, handleViewClient, selectedClient, setSelectedClient,
        showDeleteModal, onArchiveClient } = useManageClients()

    const [showAddModal, setShowAddModal] = useState(false);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editingClient, setEditingClient] = useState<any | null>(null);

    // const handleViewClient = (client: Client) => {
    //     setSelectedClient(client);
    //     setEditingClient({...client});
    //     setEditMode(false);
    //     setShowViewModal(true);
    // };

    const handleDeleteClient = async (clientId: number) => {
        if (window.confirm('Are you sure you want to delete this client?')) {
            await onArchiveClient(clientId);
        }
    };

    const formatCurrency = (amount: number) => {
        return `$${amount?.toLocaleString()}`;
    };


    return (
        <div className="min-h-screen bg-gray-50">
            <main className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
                    {/*<div className="flex items-center space-x-2">*/}
                    {/*    <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">*/}
                    {/*        <span className="text-sm font-medium text-gray-700">All Time</span>*/}
                    {/*        <ChevronDown className="h-4 w-4 text-gray-500" />*/}
                    {/*    </button>*/}
                    {/*</div>*/}
                </div>

                {/* Client Directory */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-4 sm:p-6 border-b border-gray-200">
                        {/* Phones: actions wrap under the title so Add Client stays on screen */}
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Client Directory</h2>

                            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                <button
                                    onClick={exportClients}
                                    className="flex items-center space-x-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    <Download className="h-4 w-4" />
                                    <span className="text-sm font-medium">Export</span>
                                </button>
                                <button
                                    onClick={() => setShowBulkUploadModal(true)}
                                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <Plus className="h-4 w-4" />
                                    <span className="text-sm font-medium">Import</span>
                                </button>
                                <button
                                    onClick={() => setShowAddModal(true)}
                                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <Plus className="h-4 w-4" />
                                    <span className="text-sm font-medium">Add Client</span>
                                </button>
                            </div>
                        </div>

                        <div className="mt-4">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search clients..."
                                    value={searchTerm}
                                    onChange={(e) => onSearchChange(e.target.value)}
                                    className="w-full max-w-md pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                {isSearching && (
                                    <div className="absolute right-3 top-2.5 h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Clients Table — loader replaces only the table so the search input keeps focus */}
                    {loading && !isSearching ? (
                        <div className="flex justify-center py-16">
                            <Loading type="dark" />
                        </div>
                    ) : (
                        <ClientTable handleDeleteClient={handleDeleteClient} handleViewClient={handleViewClient}
                            rowsPerPage={rowsPerPage}
                            onClientRowClick={onClientRowClick}
                        />
                    )}
                </div>
            </main>

            {/* Add Client Modal */}
            {showAddModal && (
                <AddClientModal setCloseParent={() => setShowAddModal(false)} />
            )}
            {showBulkUploadModal && (
                <BulkCsvUpload
                    title="Client"
                    endpoint="/v1/clients/bulk"
                    csvHeaders={["name", "email", "phone", "dob", "address", "notes"]}
                    sampleRow={{
                        name: "John Doe",
                        email: "john@example.com",
                        phone: "+919999999999",
                        dob: "1995-01-10",
                        address: "Delhi",
                        notes: "VIP client",
                    }}
                    onClose={() => setShowBulkUploadModal(false)}
                    onSuccess={async () => {
                        await fetchClients({ page: 1 });
                    }}
                />
            )}
            {showViewModal && selectedClient && (
                <ViewClientModal selectedClient={selectedClient} onClose={handleCloseViewClient} />
            )}

            {/*{*/}
            {/*    showDeleteModal && <DeleteClientModal isOpen={showDeleteModal} onOpenChange={} onArchiveClient={} isArchivingClient={} client={selectedClient} />*/}
            {/*    */}
            {/*}*/}
        </div>
    );
}

export default ClientsPage;
